import React, { useState, useEffect, useRef } from 'react';
import { User } from '../../services/authService';
import { KnowledgeCategoryService, KnowledgeCategory } from '../../services/knowledgeCategoryService';
import { makeAuthenticatedRequest } from '../../services/authService';
import { getActivityCounts } from '../../services/activityService';
import PageHeader from '../common/PageHeader';
import KnowledgeCategoriesChart from './KnowledgeCategoriesChart';
import TasksChart from './TasksChart';
import BlocksChart from './BlocksChart';
import HabitsChart from './HabitsChart';
import LessonsChart from './LessonsChart';
import TimeLogsChart from './TimeLogsChart';
import ActivitiesChart from './ActivitiesChart';
import DailyNotesChart from './DailyNotesChart';
import CashflowChart from './CashflowChart';
import TokenUsageChart from './TokenUsageChart';
import './Dashboard.css';

interface DashboardProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface CategoryLessonData {
  categoryName: string;
  categoryId: string;
  lessonsCount: number;
}

interface TaskChartData {
  period: string;
  completed: number;
  newTasks: number;
}

interface BlockChartData {
  date: string;
  count: number;
}

interface HabitChartData {
  date: string;
  count: number;
}

interface LessonChartData {
  date: string;
  count: number;
}

interface TimeLogChartData {
  date: string;
  minutes: number;
}

interface ActivityChartData {
  date: string;
  count: number;
}

interface DailyNotesChartData {
  date: string;
  count: number;
}

interface CashflowChartData {
  period: string;
  income: number;
  spend: number;
}

interface TokenUsageChartData {
  date: string;
  [modelKey: string]: string | number | undefined;
}

const Dashboard: React.FC<DashboardProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [blocksLoading, setBlocksLoading] = useState(true);
  const [blocksError, setBlocksError] = useState<string | null>(null);
  const [habitsLoading, setHabitsLoading] = useState(true);
  const [habitsError, setHabitsError] = useState<string | null>(null);
  const [lessonsLoading, setLessonsLoading] = useState(true);
  const [lessonsError, setLessonsError] = useState<string | null>(null);
  const [timelogsLoading, setTimelogsLoading] = useState(true);
  const [timelogsError, setTimelogsError] = useState<string | null>(null);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState<string | null>(null);
  const [dailyNotesLoading, setDailyNotesLoading] = useState(true);
  const [dailyNotesError, setDailyNotesError] = useState<string | null>(null);
  const [cashflowLoading, setCashflowLoading] = useState(true);
  const [cashflowError, setCashflowError] = useState<string | null>(null);
  const [tokenUsageLoading, setTokenUsageLoading] = useState(true);
  const [tokenUsageError, setTokenUsageError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<CategoryLessonData[]>([]);
  const [taskChartData, setTaskChartData] = useState<TaskChartData[]>([]);
  const [blockChartData, setBlockChartData] = useState<BlockChartData[]>([]);
  const [habitChartData, setHabitChartData] = useState<HabitChartData[]>([]);
  const [lessonChartData, setLessonChartData] = useState<LessonChartData[]>([]);
  const [timelogChartData, setTimelogChartData] = useState<TimeLogChartData[]>([]);
  const [activityChartData, setActivityChartData] = useState<ActivityChartData[]>([]);
  const [dailyNotesChartData, setDailyNotesChartData] = useState<DailyNotesChartData[]>([]);
  const [cashflowChartData, setCashflowChartData] = useState<CashflowChartData[]>([]);
  const [tokenUsageChartData, setTokenUsageChartData] = useState<TokenUsageChartData[]>([]);
  
  // Collapsible state for each chart
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [tasksExpanded, setTasksExpanded] = useState(true);
  const [blocksExpanded, setBlocksExpanded] = useState(true);
  const [habitsExpanded, setHabitsExpanded] = useState(true);
  const [lessonsExpanded, setLessonsExpanded] = useState(true);
  const [timelogsExpanded, setTimelogsExpanded] = useState(true);
  const [activitiesExpanded, setActivitiesExpanded] = useState(true);
  const [dailyNotesExpanded, setDailyNotesExpanded] = useState(true);
  const [cashflowExpanded, setCashflowExpanded] = useState(true);
  const [tokenUsageExpanded, setTokenUsageExpanded] = useState(true);

  // Menu and chart options state
  const [showMenu, setShowMenu] = useState(false);
  const [chartViewType, setChartViewType] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('daily');
  const [timeRange, setTimeRange] = useState<{ startDate: Date; endDate: Date }>(() => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 13);
    startDate.setHours(0, 0, 0, 0);
    return { startDate, endDate };
  });
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  // Click outside handler for menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        setShowCustomRange(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    loadCategoriesData();
    loadLessonsData();
    if (user?.id) {
      loadTaskData(user.id.toString());
      loadBlocksData(user.id);
      loadHabitsData(user.id);
      loadTimeLogsData(user.id);
      loadActivitiesData();
      loadDailyNotesData(user.id);
      loadCashflowData(user.id);
      loadTokenUsageData(user.id);
    } else {
      setTasksLoading(false);
      setBlocksLoading(false);
      setHabitsLoading(false);
      setTimelogsLoading(false);
      setActivitiesLoading(false);
      setDailyNotesLoading(false);
      setCashflowLoading(false);
      setTokenUsageLoading(false);
    }
  }, [user, chartViewType, timeRange]);

  // Helper function to set time range from preset (days)
  const setTimeRangePreset = (days: number) => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (days - 1));
    startDate.setHours(0, 0, 0, 0);
    setTimeRange({ startDate, endDate });
    setShowMenu(false);
  };

  // Helper function to set time range from preset (years)
  const setTimeRangePresetYears = (years: number) => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    const startDate = new Date(endDate);
    startDate.setFullYear(startDate.getFullYear() - (years - 1));
    startDate.setMonth(0);
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);
    setTimeRange({ startDate, endDate });
    setShowMenu(false);
  };

  // Helper function to set custom time range
  const handleCustomRangeSubmit = () => {
    if (customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999);
      setTimeRange({ startDate, endDate });
      setShowCustomRange(false);
      setShowMenu(false);
    }
  };

  // Helper function to parse date string (YYYY-MM-DD) as local date (not UTC)
  const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Helper function to get date key for matching (YYYY-MM-DD)
  const getDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format date for input
  const formatDateForInput = (date: Date): string => {
    return getDateKey(date);
  };

  // Helper function to format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date: Date): string => {
    return getDateKey(date);
  };

  // Helper function to format date label based on view type
  const formatDateLabel = (date: Date, viewType: 'daily' | 'weekly' | 'monthly' | 'yearly'): string => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    
    if (viewType === 'daily') {
      return `${month}/${day}`;
    } else if (viewType === 'weekly') {
      // For weekly, show the start date of the week
      return `${month}/${day}`;
    } else if (viewType === 'monthly') {
      // For monthly, show month/year
      return `${month}/${year}`;
    } else {
      // For yearly, show year only
      return `${year}`;
    }
  };

  // Helper function to get start of week (Monday = 0, matching MySQL WEEKDAY)
  const getStartOfWeek = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    // JavaScript getDay(): 0=Sunday, 1=Monday, ..., 6=Saturday
    // MySQL WEEKDAY(): 0=Monday, 1=Tuesday, ..., 6=Sunday
    // Convert to MySQL behavior: Monday = 0
    const weekday = day === 0 ? 6 : day - 1; // Sunday becomes 6, Monday becomes 0
    const diff = d.getDate() - weekday;
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Helper function to get start of month
  const getStartOfMonth = (date: Date): Date => {
    const d = new Date(date);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Helper function to get start of year
  const getStartOfYear = (date: Date): Date => {
    const d = new Date(date);
    d.setMonth(0);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Helper function to generate complete date range for daily view
  const generateDailyDateRange = (startDate: Date, endDate: Date): Date[] => {
    const dates: Date[] = [];
    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  // Helper function to generate complete date range for weekly view
  const generateWeeklyDateRange = (startDate: Date, endDate: Date): Date[] => {
    const weeks: Date[] = [];
    let currentWeekStart = getStartOfWeek(startDate);
    const endWeekStart = getStartOfWeek(endDate);
    
    while (currentWeekStart <= endWeekStart) {
      weeks.push(new Date(currentWeekStart));
      currentWeekStart = new Date(currentWeekStart);
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    }
    return weeks;
  };

  // Helper function to generate complete date range for monthly view
  const generateMonthlyDateRange = (startDate: Date, endDate: Date): Date[] => {
    const months: Date[] = [];
    let currentMonthStart = getStartOfMonth(startDate);
    const endMonthStart = getStartOfMonth(endDate);
    
    while (currentMonthStart <= endMonthStart) {
      months.push(new Date(currentMonthStart));
      currentMonthStart = new Date(currentMonthStart.getFullYear(), currentMonthStart.getMonth() + 1, 1);
      currentMonthStart.setHours(0, 0, 0, 0);
    }
    return months;
  };

  // Helper function to generate complete date range for yearly view
  const generateYearlyDateRange = (startDate: Date, endDate: Date): Date[] => {
    const years: Date[] = [];
    let currentYearStart = getStartOfYear(startDate);
    const endYearStart = getStartOfYear(endDate);
    
    while (currentYearStart <= endYearStart) {
      years.push(new Date(currentYearStart));
      currentYearStart = new Date(currentYearStart.getFullYear() + 1, 0, 1);
      currentYearStart.setHours(0, 0, 0, 0);
    }
    return years;
  };

  const loadCategoriesData = async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError(null);

      // Fetch all categories with lesson counts in one API call
      const result = await KnowledgeCategoryService.getCategoriesWithCounts();
      const categories = result.categories;

      // Transform to chart data format
      const data = categories.map((category) => ({
        categoryName: category.category_name,
        categoryId: category.category_id,
        lessonsCount: category.lessons_count || 0
      }));
      
      // Sort by lessons count (descending), then alphabetically
      const sortedData = data.sort((a, b) => {
        if (b.lessonsCount !== a.lessonsCount) {
          return b.lessonsCount - a.lessonsCount;
        }
        return a.categoryName.localeCompare(b.categoryName);
      });

      setChartData(sortedData);
    } catch (err) {
      console.error('Error loading categories data:', err);
      setCategoriesError(err instanceof Error ? err.message : 'Failed to load categories data');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const loadTaskData = async (userId: string) => {
    try {
      setTasksLoading(true);
      setTasksError(null);

      const { startDate, endDate } = timeRange;
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      const groupBy = chartViewType === 'daily' ? 'day' : chartViewType === 'weekly' ? 'week' : chartViewType === 'monthly' ? 'month' : 'year';
      
      const response = await makeAuthenticatedRequest(
        `/tasks/user/${userId}/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=${groupBy}`,
        { method: 'GET' }
      );
      
      if (!response.success || !response.counts) {
        setTaskChartData([]);
        setTasksLoading(false);
        return;
      }

      const counts = response.counts;
      const dataMap = new Map<string, { completed: number; newTasks: number }>();
      
      counts.forEach((item: any) => {
        const periodDate = parseLocalDate(item.period_date);
        const dateKey = getDateKey(periodDate);
        dataMap.set(dateKey, {
          completed: parseInt(item.completed) || 0,
          newTasks: parseInt(item.new_tasks) || 0
        });
      });

      const periods: TaskChartData[] = [];
      let dateRange: Date[] = [];
      
      if (chartViewType === 'daily') {
        dateRange = generateDailyDateRange(startDate, endDate);
      } else if (chartViewType === 'weekly') {
        dateRange = generateWeeklyDateRange(startDate, endDate);
      } else if (chartViewType === 'monthly') {
        dateRange = generateMonthlyDateRange(startDate, endDate);
      } else {
        dateRange = generateYearlyDateRange(startDate, endDate);
      }
      
      dateRange.forEach((periodDate) => {
        const dateKey = getDateKey(periodDate);
        const data = dataMap.get(dateKey) || { completed: 0, newTasks: 0 };
        let periodLabel: string;
        
        if (chartViewType === 'daily') {
          periodLabel = formatDateLabel(periodDate, 'daily');
        } else if (chartViewType === 'weekly') {
          const weekEnd = new Date(periodDate);
          weekEnd.setDate(weekEnd.getDate() + 6);
          const startMonth = periodDate.getMonth() + 1;
          const startDay = periodDate.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();
          periodLabel = `${startMonth}/${startDay}-${endMonth}/${endDay}`;
        } else if (chartViewType === 'monthly') {
          periodLabel = formatDateLabel(periodDate, 'monthly');
        } else {
          periodLabel = formatDateLabel(periodDate, 'yearly');
        }
        
        periods.push({
          period: periodLabel,
          completed: data.completed,
          newTasks: data.newTasks
        });
      });
      
      setTaskChartData(periods);
    } catch (err) {
      console.error('Error loading task data:', err);
      setTasksError(err instanceof Error ? err.message : 'Failed to load task data');
      setTaskChartData([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const loadBlocksData = async (userId: number) => {
    try {
      setBlocksLoading(true);
      setBlocksError(null);

      const { startDate, endDate } = timeRange;
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      const groupBy = chartViewType === 'daily' ? 'day' : chartViewType === 'weekly' ? 'week' : chartViewType === 'monthly' ? 'month' : 'year';
      
      const response = await makeAuthenticatedRequest(
        `/blocks/user/${userId}/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=${groupBy}`,
        { method: 'GET' }
      );
      
      if (!response.success || !response.data) {
        setBlockChartData([]);
        setBlocksLoading(false);
        return;
      }

      const counts = response.data;
      const dataMap = new Map<string, number>();
      
      counts.forEach((item: any) => {
        const periodDate = parseLocalDate(item.period_date);
        const dateKey = getDateKey(periodDate);
        dataMap.set(dateKey, parseInt(item.count) || 0);
      });

      const periods: BlockChartData[] = [];
      let dateRange: Date[] = [];
      
      if (chartViewType === 'daily') {
        dateRange = generateDailyDateRange(startDate, endDate);
      } else if (chartViewType === 'weekly') {
        dateRange = generateWeeklyDateRange(startDate, endDate);
      } else if (chartViewType === 'monthly') {
        dateRange = generateMonthlyDateRange(startDate, endDate);
      } else {
        dateRange = generateYearlyDateRange(startDate, endDate);
      }
      
      dateRange.forEach((periodDate) => {
        const dateKey = getDateKey(periodDate);
        const count = dataMap.get(dateKey) || 0;
        let dateLabel: string;
        
        if (chartViewType === 'daily') {
          dateLabel = formatDateLabel(periodDate, 'daily');
        } else if (chartViewType === 'weekly') {
          const weekEnd = new Date(periodDate);
          weekEnd.setDate(weekEnd.getDate() + 6);
          const startMonth = periodDate.getMonth() + 1;
          const startDay = periodDate.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();
          dateLabel = `${startMonth}/${startDay}-${endMonth}/${endDay}`;
        } else if (chartViewType === 'monthly') {
          dateLabel = formatDateLabel(periodDate, 'monthly');
        } else {
          dateLabel = formatDateLabel(periodDate, 'yearly');
        }
        
        periods.push({
          date: dateLabel,
          count: count
        });
      });
      
      setBlockChartData(periods);
    } catch (err) {
      console.error('Error loading blocks data:', err);
      setBlocksError(err instanceof Error ? err.message : 'Failed to load blocks data');
      setBlockChartData([]);
    } finally {
      setBlocksLoading(false);
    }
  };

  const loadHabitsData = async (userId: number) => {
    try {
      setHabitsLoading(true);
      setHabitsError(null);

      const { startDate, endDate } = timeRange;
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      const groupBy = chartViewType === 'daily' ? 'day' : chartViewType === 'weekly' ? 'week' : chartViewType === 'monthly' ? 'month' : 'year';
      
      const response = await makeAuthenticatedRequest(
        `/habit-completions/user/${userId}/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=${groupBy}`,
        { method: 'GET' }
      );
      
      if (!response.success || !response.counts) {
        setHabitChartData([]);
        setHabitsLoading(false);
        return;
      }

      const counts = response.counts;
      const dataMap = new Map<string, number>();
      
      counts.forEach((item: any) => {
        const periodDate = parseLocalDate(item.period_date);
        const dateKey = getDateKey(periodDate);
        dataMap.set(dateKey, parseInt(item.count) || 0);
      });

      const periods: HabitChartData[] = [];
      let dateRange: Date[] = [];
      
      if (chartViewType === 'daily') {
        dateRange = generateDailyDateRange(startDate, endDate);
      } else if (chartViewType === 'weekly') {
        dateRange = generateWeeklyDateRange(startDate, endDate);
      } else if (chartViewType === 'monthly') {
        dateRange = generateMonthlyDateRange(startDate, endDate);
      } else {
        dateRange = generateYearlyDateRange(startDate, endDate);
      }
      
      dateRange.forEach((periodDate) => {
        const dateKey = getDateKey(periodDate);
        const count = dataMap.get(dateKey) || 0;
        let dateLabel: string;
        
        if (chartViewType === 'daily') {
          dateLabel = formatDateLabel(periodDate, 'daily');
        } else if (chartViewType === 'weekly') {
          const weekEnd = new Date(periodDate);
          weekEnd.setDate(weekEnd.getDate() + 6);
          const startMonth = periodDate.getMonth() + 1;
          const startDay = periodDate.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();
          dateLabel = `${startMonth}/${startDay}-${endMonth}/${endDay}`;
        } else if (chartViewType === 'monthly') {
          dateLabel = formatDateLabel(periodDate, 'monthly');
        } else {
          dateLabel = formatDateLabel(periodDate, 'yearly');
        }
        
        periods.push({
          date: dateLabel,
          count: count
        });
      });
      
      setHabitChartData(periods);
    } catch (err) {
      console.error('Error loading habits data:', err);
      setHabitsError(err instanceof Error ? err.message : 'Failed to load habits data');
      setHabitChartData([]);
    } finally {
      setHabitsLoading(false);
    }
  };

  const loadLessonsData = async () => {
    try {
      setLessonsLoading(true);
      setLessonsError(null);

      const { startDate, endDate } = timeRange;
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      const groupBy = chartViewType === 'daily' ? 'day' : chartViewType === 'weekly' ? 'week' : chartViewType === 'monthly' ? 'month' : 'year';
      
      const response = await makeAuthenticatedRequest(
        `/lessons/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=${groupBy}`,
        { method: 'GET' }
      );
      
      if (!response.success || !response.counts) {
        setLessonChartData([]);
        setLessonsLoading(false);
        return;
      }

      const counts = response.counts;
      const dataMap = new Map<string, number>();
      
      counts.forEach((item: any) => {
        const periodDate = parseLocalDate(item.period_date);
        const dateKey = getDateKey(periodDate);
        dataMap.set(dateKey, parseInt(item.count) || 0);
      });

      const periods: LessonChartData[] = [];
      let dateRange: Date[] = [];
      
      if (chartViewType === 'daily') {
        dateRange = generateDailyDateRange(startDate, endDate);
      } else if (chartViewType === 'weekly') {
        dateRange = generateWeeklyDateRange(startDate, endDate);
      } else if (chartViewType === 'monthly') {
        dateRange = generateMonthlyDateRange(startDate, endDate);
      } else {
        dateRange = generateYearlyDateRange(startDate, endDate);
      }
      
      dateRange.forEach((periodDate) => {
        const dateKey = getDateKey(periodDate);
        const count = dataMap.get(dateKey) || 0;
        let dateLabel: string;
        
        if (chartViewType === 'daily') {
          dateLabel = formatDateLabel(periodDate, 'daily');
        } else if (chartViewType === 'weekly') {
          const weekEnd = new Date(periodDate);
          weekEnd.setDate(weekEnd.getDate() + 6);
          const startMonth = periodDate.getMonth() + 1;
          const startDay = periodDate.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();
          dateLabel = `${startMonth}/${startDay}-${endMonth}/${endDay}`;
        } else if (chartViewType === 'monthly') {
          dateLabel = formatDateLabel(periodDate, 'monthly');
        } else {
          dateLabel = formatDateLabel(periodDate, 'yearly');
        }
        
        periods.push({
          date: dateLabel,
          count: count
        });
      });
      
      setLessonChartData(periods);
    } catch (err) {
      console.error('Error loading lessons data:', err);
      setLessonsError(err instanceof Error ? err.message : 'Failed to load lessons data');
      setLessonChartData([]);
    } finally {
      setLessonsLoading(false);
    }
  };

  const loadTimeLogsData = async (userId: number) => {
    try {
      setTimelogsLoading(true);
      setTimelogsError(null);

      const { startDate, endDate } = timeRange;
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      const groupBy = chartViewType === 'daily' ? 'day' : chartViewType === 'weekly' ? 'week' : chartViewType === 'monthly' ? 'month' : 'year';
      
      const response = await makeAuthenticatedRequest(
        `/timelogs/user/${userId}/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=${groupBy}`,
        { method: 'GET' }
      );
      
      if (!response.success || !response.data) {
        setTimelogChartData([]);
        setTimelogsLoading(false);
        return;
      }

      const counts = response.data;
      const dataMap = new Map<string, number>();
      
      counts.forEach((item: any) => {
        const periodDate = parseLocalDate(item.period_date);
        const dateKey = getDateKey(periodDate);
        dataMap.set(dateKey, parseInt(item.minutes) || 0);
      });

      const periods: TimeLogChartData[] = [];
      let dateRange: Date[] = [];
      
      if (chartViewType === 'daily') {
        dateRange = generateDailyDateRange(startDate, endDate);
      } else if (chartViewType === 'weekly') {
        dateRange = generateWeeklyDateRange(startDate, endDate);
      } else if (chartViewType === 'monthly') {
        dateRange = generateMonthlyDateRange(startDate, endDate);
      } else {
        dateRange = generateYearlyDateRange(startDate, endDate);
      }
      
      dateRange.forEach((periodDate) => {
        const dateKey = getDateKey(periodDate);
        const minutes = dataMap.get(dateKey) || 0;
        let dateLabel: string;
        
        if (chartViewType === 'daily') {
          dateLabel = formatDateLabel(periodDate, 'daily');
        } else if (chartViewType === 'weekly') {
          const weekEnd = new Date(periodDate);
          weekEnd.setDate(weekEnd.getDate() + 6);
          const startMonth = periodDate.getMonth() + 1;
          const startDay = periodDate.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();
          dateLabel = `${startMonth}/${startDay}-${endMonth}/${endDay}`;
        } else if (chartViewType === 'monthly') {
          dateLabel = formatDateLabel(periodDate, 'monthly');
        } else {
          dateLabel = formatDateLabel(periodDate, 'yearly');
        }
        
        periods.push({
          date: dateLabel,
          minutes: minutes
        });
      });
      
      setTimelogChartData(periods);
    } catch (err) {
      console.error('Error loading timelogs data:', err);
      setTimelogsError(err instanceof Error ? err.message : 'Failed to load timelogs data');
      setTimelogChartData([]);
    } finally {
      setTimelogsLoading(false);
    }
  };

  const loadActivitiesData = async () => {
    try {
      setActivitiesLoading(true);
      setActivitiesError(null);

      const { startDate, endDate } = timeRange;
      
      // Format dates for API (YYYY-MM-DD)
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      // Fetch activity counts for the date range (optimized endpoint)
      const response = await getActivityCounts(startDateStr, endDateStr);
      
      if (!response.success || !response.counts_by_date) {
        setActivityChartData([]);
        setActivitiesLoading(false);
        return;
      }

      const countsByDate = response.counts_by_date;
      const periods: ActivityChartData[] = [];
      
      if (chartViewType === 'daily') {
        const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        const maxDays = Math.min(daysDiff, 60);
        
        for (let i = maxDays - 1; i >= 0; i--) {
          const periodEnd = new Date(endDate);
          periodEnd.setDate(periodEnd.getDate() - i);
          periodEnd.setHours(23, 59, 59, 999);
          
          const periodStart = new Date(periodEnd);
          periodStart.setHours(0, 0, 0, 0);
          
          const dateLabel = formatDateLabel(periodStart, 'daily');
          const dateKey = getDateKey(periodStart);
          
          const count = countsByDate[dateKey] || 0;
          
          periods.push({ date: dateLabel, count });
        }
      } else if (chartViewType === 'weekly') {
        let currentWeekStart = getStartOfWeek(startDate);
        const endWeekStart = getStartOfWeek(endDate);
        const maxWeeks = 52;
        
        let weekCount = 0;
        while (currentWeekStart <= endWeekStart && weekCount < maxWeeks) {
          const weekEnd = new Date(currentWeekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);
          weekEnd.setHours(23, 59, 59, 999);
          
          const startMonth = currentWeekStart.getMonth() + 1;
          const startDay = currentWeekStart.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();
          const dateLabel = `${startMonth}/${startDay}-${endMonth}/${endDay}`;
          
          // Sum counts for all days in the week
          let count = 0;
          const currentDate = new Date(currentWeekStart);
          while (currentDate <= weekEnd) {
            const dateKey = getDateKey(currentDate);
            count += countsByDate[dateKey] || 0;
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          periods.push({ date: dateLabel, count });
          
          currentWeekStart = new Date(currentWeekStart);
          currentWeekStart.setDate(currentWeekStart.getDate() + 7);
          weekCount++;
        }
      } else if (chartViewType === 'monthly') {
        let currentMonthStart = getStartOfMonth(startDate);
        const endMonthStart = getStartOfMonth(endDate);
        const maxMonths = 24;
        
        let monthCount = 0;
        while (currentMonthStart <= endMonthStart && monthCount < maxMonths) {
          const monthEnd = new Date(currentMonthStart.getFullYear(), currentMonthStart.getMonth() + 1, 0);
          monthEnd.setHours(23, 59, 59, 999);
          
          const dateLabel = formatDateLabel(currentMonthStart, 'monthly');
          
          // Sum counts for all days in the month
          let count = 0;
          const currentDate = new Date(currentMonthStart);
          while (currentDate <= monthEnd) {
            const dateKey = getDateKey(currentDate);
            count += countsByDate[dateKey] || 0;
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          periods.push({ date: dateLabel, count });
          
          currentMonthStart = new Date(currentMonthStart.getFullYear(), currentMonthStart.getMonth() + 1, 1);
          monthCount++;
        }
      } else {
        let currentYearStart = getStartOfYear(startDate);
        const endYearStart = getStartOfYear(endDate);
        const maxYears = 20;
        
        let yearCount = 0;
        while (currentYearStart <= endYearStart && yearCount < maxYears) {
          const yearEnd = new Date(currentYearStart.getFullYear(), 11, 31);
          yearEnd.setHours(23, 59, 59, 999);
          
          const dateLabel = formatDateLabel(currentYearStart, 'yearly');
          
          // Sum counts for all days in the year
          let count = 0;
          const currentDate = new Date(currentYearStart);
          while (currentDate <= yearEnd && currentDate.getFullYear() === currentYearStart.getFullYear()) {
            const dateKey = getDateKey(currentDate);
            count += countsByDate[dateKey] || 0;
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          periods.push({ date: dateLabel, count });
          
          currentYearStart = new Date(currentYearStart.getFullYear() + 1, 0, 1);
          yearCount++;
        }
      }
      
      setActivityChartData(periods);
    } catch (err) {
      console.error('Error loading activities data:', err);
      setActivitiesError(err instanceof Error ? err.message : 'Failed to load activities data');
      setActivityChartData([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const loadDailyNotesData = async (userId: number) => {
    try {
      setDailyNotesLoading(true);
      setDailyNotesError(null);

      const { startDate, endDate } = timeRange;
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      const groupBy = chartViewType === 'daily' ? 'day' : chartViewType === 'weekly' ? 'week' : chartViewType === 'monthly' ? 'month' : 'year';
      
      const response = await makeAuthenticatedRequest(
        `/daily-notes/user/${userId}/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=${groupBy}`,
        { method: 'GET' }
      );
      
      if (!response.success || !response.data) {
        setDailyNotesChartData([]);
        setDailyNotesLoading(false);
        return;
      }

      const counts = response.data;
      const dataMap = new Map<string, number>();
      
      counts.forEach((item: any) => {
        const periodDate = parseLocalDate(item.period_date);
        const dateKey = getDateKey(periodDate);
        dataMap.set(dateKey, parseInt(item.count) || 0);
      });

      const periods: DailyNotesChartData[] = [];
      let dateRange: Date[] = [];
      
      if (chartViewType === 'daily') {
        dateRange = generateDailyDateRange(startDate, endDate);
      } else if (chartViewType === 'weekly') {
        dateRange = generateWeeklyDateRange(startDate, endDate);
      } else if (chartViewType === 'monthly') {
        dateRange = generateMonthlyDateRange(startDate, endDate);
      } else {
        dateRange = generateYearlyDateRange(startDate, endDate);
      }
      
      dateRange.forEach((periodDate) => {
        const dateKey = getDateKey(periodDate);
        const count = dataMap.get(dateKey) || 0;
        let dateLabel: string;
        
        if (chartViewType === 'daily') {
          dateLabel = formatDateLabel(periodDate, 'daily');
        } else if (chartViewType === 'weekly') {
          const weekEnd = new Date(periodDate);
          weekEnd.setDate(weekEnd.getDate() + 6);
          const startMonth = periodDate.getMonth() + 1;
          const startDay = periodDate.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();
          dateLabel = `${startMonth}/${startDay}-${endMonth}/${endDay}`;
        } else if (chartViewType === 'monthly') {
          dateLabel = formatDateLabel(periodDate, 'monthly');
        } else {
          dateLabel = formatDateLabel(periodDate, 'yearly');
        }
        
        periods.push({
          date: dateLabel,
          count: count
        });
      });
      
      setDailyNotesChartData(periods);
    } catch (err) {
      console.error('Error loading daily notes data:', err);
      setDailyNotesError(err instanceof Error ? err.message : 'Failed to load daily notes data');
      setDailyNotesChartData([]);
    } finally {
      setDailyNotesLoading(false);
    }
  };

  const loadCashflowData = async (userId: number) => {
    try {
      setCashflowLoading(true);
      setCashflowError(null);

      const { startDate, endDate } = timeRange;
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      const groupBy = chartViewType === 'daily' ? 'day' : chartViewType === 'weekly' ? 'week' : chartViewType === 'monthly' ? 'month' : 'year';
      
      const response = await makeAuthenticatedRequest(
        `/cashflow/user/${userId}/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=${groupBy}`,
        { method: 'GET' }
      );
      
      if (!response.success || !response.data) {
        setCashflowChartData([]);
        setCashflowLoading(false);
        return;
      }

      const counts = response.data;
      const dataMap = new Map<string, { income: number; spend: number }>();
      
      counts.forEach((item: any) => {
        // Handle ISO datetime strings (e.g., "2025-11-09T17:00:00.000Z") or date strings (e.g., "2025-11-09")
        let dateStr = item.period_date;
        if (dateStr && dateStr.includes('T')) {
          // Extract date part from ISO datetime string
          dateStr = dateStr.split('T')[0];
        }
        const periodDate = parseLocalDate(dateStr);
        const dateKey = getDateKey(periodDate);
        dataMap.set(dateKey, {
          income: parseFloat(item.income) || 0,
          spend: parseFloat(item.spend) || 0
        });
      });

      const periods: CashflowChartData[] = [];
      let dateRange: Date[] = [];
      
      if (chartViewType === 'daily') {
        dateRange = generateDailyDateRange(startDate, endDate);
      } else if (chartViewType === 'weekly') {
        dateRange = generateWeeklyDateRange(startDate, endDate);
      } else if (chartViewType === 'monthly') {
        dateRange = generateMonthlyDateRange(startDate, endDate);
      } else {
        dateRange = generateYearlyDateRange(startDate, endDate);
      }
      
      dateRange.forEach((periodDate) => {
        const dateKey = getDateKey(periodDate);
        const data = dataMap.get(dateKey) || { income: 0, spend: 0 };
        let periodLabel: string;
        
        if (chartViewType === 'daily') {
          periodLabel = formatDateLabel(periodDate, 'daily');
        } else if (chartViewType === 'weekly') {
          const weekEnd = new Date(periodDate);
          weekEnd.setDate(weekEnd.getDate() + 6);
          const startMonth = periodDate.getMonth() + 1;
          const startDay = periodDate.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();
          periodLabel = `${startMonth}/${startDay}-${endMonth}/${endDay}`;
        } else if (chartViewType === 'monthly') {
          periodLabel = formatDateLabel(periodDate, 'monthly');
        } else {
          periodLabel = formatDateLabel(periodDate, 'yearly');
        }
        
        periods.push({
          period: periodLabel,
          income: data.income,
          spend: data.spend
        });
      });
      
      setCashflowChartData(periods);
    } catch (err) {
      console.error('Error loading cashflow data:', err);
      setCashflowError(err instanceof Error ? err.message : 'Failed to load cashflow data');
      setCashflowChartData([]);
    } finally {
      setCashflowLoading(false);
    }
  };

  const loadTokenUsageData = async (userId: number) => {
    try {
      setTokenUsageLoading(true);
      setTokenUsageError(null);

      const { startDate, endDate } = timeRange;
      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);
      const groupBy = chartViewType === 'daily' ? 'day' : chartViewType === 'weekly' ? 'week' : chartViewType === 'monthly' ? 'month' : 'year';
      
      const response = await makeAuthenticatedRequest(
        `/token-usage/user/${userId}/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=${groupBy}`,
        { method: 'GET' }
      );
      
      if (!response.success || !response.data) {
        setTokenUsageChartData([]);
        setTokenUsageLoading(false);
        return;
      }

      const counts = response.data;
      const dataMap = new Map<string, { [modelKey: string]: { total: number; input: number; output: number; provider: string } }>();
      const allModels = new Set<string>();
      
      counts.forEach((item: any) => {
        const periodDate = parseLocalDate(item.period_date);
        const dateKey = getDateKey(periodDate);
        const provider = item.provider;
        const model = item.model;
        const modelKey = `${provider}-${model}`;
        
        allModels.add(modelKey);
        
        if (!dataMap.has(dateKey)) {
          dataMap.set(dateKey, {});
        }
        
        const periodData = dataMap.get(dateKey)!;
        periodData[modelKey] = {
          total: parseInt(item.total_tokens) || 0,
          input: parseInt(item.input_tokens) || 0,
          output: parseInt(item.output_tokens) || 0,
          provider: provider
        };
      });

      const periods: TokenUsageChartData[] = [];
      let dateRange: Date[] = [];
      
      if (chartViewType === 'daily') {
        dateRange = generateDailyDateRange(startDate, endDate);
      } else if (chartViewType === 'weekly') {
        dateRange = generateWeeklyDateRange(startDate, endDate);
      } else if (chartViewType === 'monthly') {
        dateRange = generateMonthlyDateRange(startDate, endDate);
      } else {
        dateRange = generateYearlyDateRange(startDate, endDate);
      }
      
      dateRange.forEach((periodDate) => {
        const dateKey = getDateKey(periodDate);
        const periodData = dataMap.get(dateKey) || {};
        let dateLabel: string;
        
        if (chartViewType === 'daily') {
          dateLabel = formatDateLabel(periodDate, 'daily');
        } else if (chartViewType === 'weekly') {
          const weekEnd = new Date(periodDate);
          weekEnd.setDate(weekEnd.getDate() + 6);
          const startMonth = periodDate.getMonth() + 1;
          const startDay = periodDate.getDate();
          const endMonth = weekEnd.getMonth() + 1;
          const endDay = weekEnd.getDate();
          dateLabel = `${startMonth}/${startDay}-${endMonth}/${endDay}`;
        } else if (chartViewType === 'monthly') {
          dateLabel = formatDateLabel(periodDate, 'monthly');
        } else {
          dateLabel = formatDateLabel(periodDate, 'yearly');
        }
        
        const periodEntry: TokenUsageChartData = { date: dateLabel };
        
        allModels.forEach((modelKey) => {
          const modelData = periodData[modelKey];
          periodEntry[modelKey] = modelData?.total || 0;
          periodEntry[`${modelKey}Input`] = modelData?.input || 0;
          periodEntry[`${modelKey}Output`] = modelData?.output || 0;
        });
        
        periods.push(periodEntry);
      });
      
      setTokenUsageChartData(periods);
    } catch (err) {
      console.error('Error loading token usage data:', err);
      setTokenUsageError(err instanceof Error ? err.message : 'Failed to load token usage data');
      setTokenUsageChartData([]);
    } finally {
      setTokenUsageLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="page-header-center-title">Dashboard</h1>
        }
        rightActions={
          <div className="dashboard-menu-container" ref={menuRef}>
            <button
              className="page-header-icon-btn"
              onClick={() => setShowMenu(!showMenu)}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="dashboard-menu-dropdown">
                <div className="dashboard-menu-section">
                  <div className="dashboard-menu-section-title">Chart View</div>
                  <button
                    className={`dashboard-menu-item ${chartViewType === 'daily' ? 'active' : ''}`}
                    onClick={() => {
                      setChartViewType('daily');
                      setShowMenu(false);
                    }}
                  >
                    Daily View
                  </button>
                  <button
                    className={`dashboard-menu-item ${chartViewType === 'weekly' ? 'active' : ''}`}
                    onClick={() => {
                      setChartViewType('weekly');
                      setShowMenu(false);
                    }}
                  >
                    Weekly View
                  </button>
                  <button
                    className={`dashboard-menu-item ${chartViewType === 'monthly' ? 'active' : ''}`}
                    onClick={() => {
                      setChartViewType('monthly');
                      setShowMenu(false);
                    }}
                  >
                    Monthly View
                  </button>
                  <button
                    className={`dashboard-menu-item ${chartViewType === 'yearly' ? 'active' : ''}`}
                    onClick={() => {
                      setChartViewType('yearly');
                      setShowMenu(false);
                    }}
                  >
                    Yearly View
                  </button>
                </div>
                <div className="dashboard-menu-divider"></div>
                <div className="dashboard-menu-section">
                  <div className="dashboard-menu-section-title">Time Range</div>
                  <button
                    className="dashboard-menu-item"
                    onClick={() => {
                      setShowCustomRange(!showCustomRange);
                      if (!showCustomRange) {
                        setCustomStartDate(formatDateForInput(timeRange.startDate));
                        setCustomEndDate(formatDateForInput(timeRange.endDate));
                      }
                    }}
                  >
                    Custom Range {showCustomRange ? '▼' : '▶'}
                  </button>
                  {showCustomRange && (
                    <div className="dashboard-custom-range">
                      <div className="dashboard-custom-range-inputs">
                        <label>
                          Start Date:
                          <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="dashboard-date-input"
                          />
                        </label>
                        <label>
                          End Date:
                          <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="dashboard-date-input"
                          />
                        </label>
                      </div>
                      <button
                        className="dashboard-menu-item"
                        onClick={handleCustomRangeSubmit}
                      >
                        Apply
                      </button>
                    </div>
                  )}
                  <button
                    className="dashboard-menu-item"
                    onClick={() => setTimeRangePreset(7)}
                  >
                    Last 7 days
                  </button>
                  <button
                    className="dashboard-menu-item"
                    onClick={() => setTimeRangePreset(30)}
                  >
                    Last 30 days
                  </button>
                  <button
                    className="dashboard-menu-item"
                    onClick={() => setTimeRangePreset(90)}
                  >
                    Last 3 months
                  </button>
                  <button
                    className="dashboard-menu-item"
                    onClick={() => setTimeRangePreset(180)}
                  >
                    Last 6 months
                  </button>
                  <button
                    className="dashboard-menu-item"
                    onClick={() => setTimeRangePresetYears(5)}
                  >
                    Last 5 years
                  </button>
                  <button
                    className="dashboard-menu-item"
                    onClick={() => setTimeRangePresetYears(10)}
                  >
                    Last 10 years
                  </button>
                  <button
                    className="dashboard-menu-item"
                    onClick={() => setTimeRangePresetYears(20)}
                  >
                    Last 20 years
                  </button>
                </div>
              </div>
            )}
          </div>
        }
      />

      <div className="dashboard-main-content">
        <div className="dashboard-charts">
          {user && (
            <div className="dashboard-chart-card">
              <button 
                className="dashboard-chart-toggle"
                onClick={() => setTasksExpanded(!tasksExpanded)}
              >
                <h2 className="dashboard-chart-title">Tasks: Completed vs New</h2>
                <span className="dashboard-chart-arrow">{tasksExpanded ? '▼' : '▶'}</span>
              </button>
              <div className={`dashboard-chart-content ${tasksExpanded ? 'expanded' : 'collapsed'}`}>
                {tasksLoading && (
                  <div className="dashboard-loading">
                    <div className="dashboard-spinner"></div>
                    <p>Loading task data...</p>
                  </div>
                )}

                {tasksError && !tasksLoading && (
                  <div className="dashboard-error">
                    <p>{tasksError}</p>
                    <button 
                      className="dashboard-retry-btn"
                      onClick={() => loadTaskData(user.id.toString())}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {!tasksLoading && !tasksError && (
                  taskChartData.length === 0 ? (
                    <div className="dashboard-empty">
                      <p>No task data available. Create tasks to see analytics.</p>
                    </div>
                  ) : (
                    <TasksChart data={taskChartData} />
                  )
                )}
              </div>
            </div>
          )}

          {user && (
            <div className="dashboard-chart-card">
              <button 
                className="dashboard-chart-toggle"
                onClick={() => setBlocksExpanded(!blocksExpanded)}
              >
                <h2 className="dashboard-chart-title">Blocks Created Per Day</h2>
                <span className="dashboard-chart-arrow">{blocksExpanded ? '▼' : '▶'}</span>
              </button>
              <div className={`dashboard-chart-content ${blocksExpanded ? 'expanded' : 'collapsed'}`}>
                {blocksLoading && (
                  <div className="dashboard-loading">
                    <div className="dashboard-spinner"></div>
                    <p>Loading blocks data...</p>
                  </div>
                )}

                {blocksError && !blocksLoading && (
                  <div className="dashboard-error">
                    <p>{blocksError}</p>
                    <button 
                      className="dashboard-retry-btn"
                      onClick={() => loadBlocksData(user.id)}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {!blocksLoading && !blocksError && (
                  blockChartData.length === 0 ? (
                    <div className="dashboard-empty">
                      <p>No block data available. Create blocks to see analytics.</p>
                    </div>
                  ) : (
                    <BlocksChart data={blockChartData} />
                  )
                )}
              </div>
            </div>
          )}

          {user && (
            <div className="dashboard-chart-card">
              <button 
                className="dashboard-chart-toggle"
                onClick={() => setHabitsExpanded(!habitsExpanded)}
              >
                <h2 className="dashboard-chart-title">Habits Completed Per Day</h2>
                <span className="dashboard-chart-arrow">{habitsExpanded ? '▼' : '▶'}</span>
              </button>
              <div className={`dashboard-chart-content ${habitsExpanded ? 'expanded' : 'collapsed'}`}>
                {habitsLoading && (
                  <div className="dashboard-loading">
                    <div className="dashboard-spinner"></div>
                    <p>Loading habits data...</p>
                  </div>
                )}

                {habitsError && !habitsLoading && (
                  <div className="dashboard-error">
                    <p>{habitsError}</p>
                    <button 
                      className="dashboard-retry-btn"
                      onClick={() => loadHabitsData(user.id)}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {!habitsLoading && !habitsError && (
                  habitChartData.length === 0 ? (
                    <div className="dashboard-empty">
                      <p>No habit data available. Complete habits to see analytics.</p>
                    </div>
                  ) : (
                    <HabitsChart data={habitChartData} />
                  )
                )}
              </div>
            </div>
          )}

          <div className="dashboard-chart-card">
            <button 
              className="dashboard-chart-toggle"
              onClick={() => setLessonsExpanded(!lessonsExpanded)}
            >
              <h2 className="dashboard-chart-title">Lessons Generated Per Day</h2>
              <span className="dashboard-chart-arrow">{lessonsExpanded ? '▼' : '▶'}</span>
            </button>
            <div className={`dashboard-chart-content ${lessonsExpanded ? 'expanded' : 'collapsed'}`}>
              {lessonsLoading && (
                <div className="dashboard-loading">
                  <div className="dashboard-spinner"></div>
                  <p>Loading lessons data...</p>
                </div>
              )}

              {lessonsError && !lessonsLoading && (
                <div className="dashboard-error">
                  <p>{lessonsError}</p>
                  <button 
                    className="dashboard-retry-btn"
                    onClick={loadLessonsData}
                  >
                    Try Again
                  </button>
                </div>
              )}

              {!lessonsLoading && !lessonsError && (
                lessonChartData.length === 0 ? (
                  <div className="dashboard-empty">
                    <p>No lesson data available. Generate lessons to see analytics.</p>
                  </div>
                ) : (
                  <LessonsChart data={lessonChartData} />
                )
              )}
            </div>
          </div>

          {user && (
            <div className="dashboard-chart-card">
              <button 
                className="dashboard-chart-toggle"
                onClick={() => setTimelogsExpanded(!timelogsExpanded)}
              >
                <h2 className="dashboard-chart-title">Time Logged Per Day</h2>
                <span className="dashboard-chart-arrow">{timelogsExpanded ? '▼' : '▶'}</span>
              </button>
              <div className={`dashboard-chart-content ${timelogsExpanded ? 'expanded' : 'collapsed'}`}>
                {timelogsLoading && (
                  <div className="dashboard-loading">
                    <div className="dashboard-spinner"></div>
                    <p>Loading timelogs data...</p>
                  </div>
                )}

                {timelogsError && !timelogsLoading && (
                  <div className="dashboard-error">
                    <p>{timelogsError}</p>
                    <button 
                      className="dashboard-retry-btn"
                      onClick={() => loadTimeLogsData(user.id)}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {!timelogsLoading && !timelogsError && (
                  timelogChartData.length === 0 ? (
                    <div className="dashboard-empty">
                      <p>No timelog data available. Log time to see analytics.</p>
                    </div>
                  ) : (
                    <TimeLogsChart data={timelogChartData} />
                  )
                )}
              </div>
            </div>
          )}

          {user && (
            <div className="dashboard-chart-card">
              <button 
                className="dashboard-chart-toggle"
                onClick={() => setActivitiesExpanded(!activitiesExpanded)}
              >
                <h2 className="dashboard-chart-title">Activities Per Day</h2>
                <span className="dashboard-chart-arrow">{activitiesExpanded ? '▼' : '▶'}</span>
              </button>
              <div className={`dashboard-chart-content ${activitiesExpanded ? 'expanded' : 'collapsed'}`}>
                {activitiesLoading && (
                  <div className="dashboard-loading">
                    <div className="dashboard-spinner"></div>
                    <p>Loading activities data...</p>
                  </div>
                )}

                {activitiesError && !activitiesLoading && (
                  <div className="dashboard-error">
                    <p>{activitiesError}</p>
                    <button 
                      className="dashboard-retry-btn"
                      onClick={loadActivitiesData}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {!activitiesLoading && !activitiesError && (
                  activityChartData.length === 0 ? (
                    <div className="dashboard-empty">
                      <p>No activity data available. Activities will appear here as you use the app.</p>
                    </div>
                  ) : (
                    <ActivitiesChart data={activityChartData} />
                  )
                )}
              </div>
            </div>
          )}

          {user && (
            <div className="dashboard-chart-card">
              <button 
                className="dashboard-chart-toggle"
                onClick={() => setDailyNotesExpanded(!dailyNotesExpanded)}
              >
                <h2 className="dashboard-chart-title">Daily Notes Generated Per Day</h2>
                <span className="dashboard-chart-arrow">{dailyNotesExpanded ? '▼' : '▶'}</span>
              </button>
              <div className={`dashboard-chart-content ${dailyNotesExpanded ? 'expanded' : 'collapsed'}`}>
                {dailyNotesLoading && (
                  <div className="dashboard-loading">
                    <div className="dashboard-spinner"></div>
                    <p>Loading daily notes data...</p>
                  </div>
                )}

                {dailyNotesError && !dailyNotesLoading && (
                  <div className="dashboard-error">
                    <p>{dailyNotesError}</p>
                    <button 
                      className="dashboard-retry-btn"
                      onClick={() => loadDailyNotesData(user.id)}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {!dailyNotesLoading && !dailyNotesError && (
                  dailyNotesChartData.length === 0 ? (
                    <div className="dashboard-empty">
                      <p>No daily notes data available. Create daily notes to see analytics.</p>
                    </div>
                  ) : (
                    <DailyNotesChart data={dailyNotesChartData} />
                  )
                )}
              </div>
            </div>
          )}

          {user && (
            <div className="dashboard-chart-card">
              <button 
                className="dashboard-chart-toggle"
                onClick={() => setCashflowExpanded(!cashflowExpanded)}
              >
                <h2 className="dashboard-chart-title">Income vs Spend</h2>
                <span className="dashboard-chart-arrow">{cashflowExpanded ? '▼' : '▶'}</span>
              </button>
              <div className={`dashboard-chart-content ${cashflowExpanded ? 'expanded' : 'collapsed'}`}>
                {cashflowLoading && (
                  <div className="dashboard-loading">
                    <div className="dashboard-spinner"></div>
                    <p>Loading cashflow data...</p>
                  </div>
                )}

                {cashflowError && !cashflowLoading && (
                  <div className="dashboard-error">
                    <p>{cashflowError}</p>
                    <button 
                      className="dashboard-retry-btn"
                      onClick={() => loadCashflowData(user.id)}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {!cashflowLoading && !cashflowError && (
                  cashflowChartData.length === 0 ? (
                    <div className="dashboard-empty">
                      <p>No cashflow data available. Add income and spend entries to see analytics.</p>
                    </div>
                  ) : (
                    <CashflowChart data={cashflowChartData} />
                  )
                )}
              </div>
            </div>
          )}

          {user && (
            <div className="dashboard-chart-card">
              <button 
                className="dashboard-chart-toggle"
                onClick={() => setTokenUsageExpanded(!tokenUsageExpanded)}
              >
                <h2 className="dashboard-chart-title">AI Token Usage</h2>
                <span className="dashboard-chart-arrow">{tokenUsageExpanded ? '▼' : '▶'}</span>
              </button>
              <div className={`dashboard-chart-content ${tokenUsageExpanded ? 'expanded' : 'collapsed'}`}>
                {tokenUsageLoading && (
                  <div className="dashboard-loading">
                    <div className="dashboard-spinner"></div>
                    <p>Loading token usage data...</p>
                  </div>
                )}

                {tokenUsageError && !tokenUsageLoading && (
                  <div className="dashboard-error">
                    <p>{tokenUsageError}</p>
                    <button 
                      className="dashboard-retry-btn"
                      onClick={() => loadTokenUsageData(user.id)}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {!tokenUsageLoading && !tokenUsageError && (
                  tokenUsageChartData.length === 0 ? (
                    <div className="dashboard-empty">
                      <p>No token usage data available. AI token usage will appear here as you use AI features.</p>
                    </div>
                  ) : (
                    <TokenUsageChart data={tokenUsageChartData} />
                  )
                )}
              </div>
            </div>
          )}

          <div className="dashboard-chart-card">
            <button 
              className="dashboard-chart-toggle"
              onClick={() => setCategoriesExpanded(!categoriesExpanded)}
            >
              <h2 className="dashboard-chart-title">Knowledge Categories & Lessons</h2>
              <span className="dashboard-chart-arrow">{categoriesExpanded ? '▼' : '▶'}</span>
            </button>
            <div className={`dashboard-chart-content ${categoriesExpanded ? 'expanded' : 'collapsed'}`}>
              {categoriesLoading && (
                <div className="dashboard-loading">
                  <div className="dashboard-spinner"></div>
                  <p>Loading categories data...</p>
                </div>
              )}

              {categoriesError && !categoriesLoading && (
                <div className="dashboard-error">
                  <p>{categoriesError}</p>
                  <button 
                    className="dashboard-retry-btn"
                    onClick={loadCategoriesData}
                  >
                    Try Again
                  </button>
                </div>
              )}

              {!categoriesLoading && !categoriesError && (
                chartData.length === 0 ? (
                  <div className="dashboard-empty">
                    <p>No categories found. Create your first knowledge category to see analytics.</p>
                  </div>
                ) : (
                  <KnowledgeCategoriesChart data={chartData} />
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

